import { type ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CommonButton } from "~/components/ui/commonButton";
import Modal from "~/components/ui/modal";
import {
  restoreFromCopyData,
  setCopyData,
  setOrganizationName,
  setOrganizationUrl,
  updateOrganization,
} from "~/slices/organizationSlice";
import type { AppDispatch, RootState } from "~/store";
import styles from "./index.module.scss";
import { OrganizationForm } from "./OrganizationForm";

const ListContent = () => {
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const isAdmin = useMemo(() => currentUser.role === "admin", [currentUser.role]);
  const { organization, isSaving } = useSelector((state: RootState) => state.organization);
  const dispatch = useDispatch<AppDispatch>();
  const [isModalOpen, setModalOpen] = useState(false);
  // Sliceから直接データを取得して詳細情報を作成
  const detailData = useMemo(() => {
    const baseData = [
      { property: "組織名", description: organization.name },
      { property: "支払いID", description: organization.payment_id || "" },
      { property: "URL", description: organization.url || "" },
    ];

    if (organization.id) {
      baseData.push({
        property: "ルートユーザー",
        description: organization.rootUser.displayName,
      });
    }

    return baseData;
  }, [
    organization.id,
    organization.name,
    organization.url,
    organization.payment_id,
    organization.rootUser.displayName,
  ]);

  // 列の定義
  const columns: ColumnDef<object>[] = [
    {
      header: "項目名",
      accessorKey: "property",
    },
    {
      header: "値",
      accessorKey: "description",
    },
  ];

  const table = useReactTable({
    data: detailData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleUpdateOrganization = async () => {
    await dispatch(updateOrganization());
    setModalOpen(false);
  };

  const handleCancel = () => {
    dispatch(restoreFromCopyData());
    setModalOpen(false);
  };

  const handleEdit = () => {
    dispatch(setCopyData());
    setModalOpen(true);
  };

  return (
    <div className={styles["listContent"]}>
      <div className="flex justify-between items-center w-full font-sans">
        <h1>詳細情報</h1>
        <div className="flex-end flex gap-[4px] flex-col">
          {isAdmin && (
            <CommonButton onClick={handleEdit} className="w-[112px]" data-testid="org-edit-button">
              編 集
            </CommonButton>
          )}
        </div>
      </div>
      <ul className={styles["ul"]}>
        {table.getRowModel().rows.map((row) => (
          <li className={styles["li"]} key={row.id}>
            {row.getVisibleCells().map((cell: any) => (
              <div key={cell.id}>
                <label style={{ width: "100%" }}>{cell.getValue() as string}</label>
              </div>
            ))}
          </li>
        ))}
      </ul>

      <Modal
        label={"詳細情報の編集"}
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        className="organizationEditModal"
        content={
          <OrganizationForm
            name={organization.name}
            url={organization.url}
            isSaving={isSaving}
            onNameChange={(value) => dispatch(setOrganizationName(value))}
            onUrlChange={(value) => dispatch(setOrganizationUrl(value))}
            onSave={handleUpdateOrganization}
            onCancel={handleCancel}
          />
        }
      />
    </div>
  );
};

export default ListContent;
